import { CdnImage, useIntl } from '@mfw/components';
import classNames from 'classnames';

import { useOrganizationContext } from '../../context/OrganizationProvider';
import { useAppThemeContext } from '../../context/AppThemeProvider';
import { useTenantContext } from '../../context/TenantProvider';
import LogoImage from '../../assets/logo/mfw-logo.png';
import messages from './messages';
import './Logo.scss';

export type LogoSize = 'extra-large' | 'large' | 'medium' | 'small';

function getLogoSize(size: LogoSize) {
  switch (size) {
    case 'extra-large':
      return 160;
    case 'large':
      return 80;
    case 'small':
      return 40;
    default:
      return 60;
  }
}

export interface LogoProps {
  className?: string;
  size?: LogoSize;
}

const Logo = ({ className, size = 'medium' }: LogoProps) => {
  const { formatMessage } = useIntl();
  const width = getLogoSize(size);
  const { tenant } = useTenantContext();
  // Reading the org context is safe even outside OrganizationProvider (e.g.
  // pre-login AuthLayout): createContextFactory returns the empty initial
  // value there, so `brandingSource` is undefined and we fall back to the
  // tenant logo.
  const { brandingSource } = useOrganizationContext();
  // The cached logo, while the live branding has not settled — the MFW mark
  // must not flash up in front of a tenant or organization that has its own.
  const { pendingLogoUrl } = useAppThemeContext();

  return (
    <CdnImage
      // `tenant.logoUrl` is the RENDERABLE half; `logoName` beside it is the
      // bare stored filename the write routes take back, and passing that to an
      // <img> resolves it against the SPA origin instead of the bucket.
      src={
        pendingLogoUrl ||
        brandingSource?.logoUrl ||
        tenant?.logoUrl ||
        LogoImage
      }
      className={classNames('Mfw-Logo', className)}
      alt={tenant?.name ?? formatMessage(messages.logoTitle)}
      height={width}
      width={width}
    />
  );
};

export default Logo;
